import { subMinutes } from "date-fns/subMinutes";
import { transformPredictionsToChartData } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/transformPredictionsToChartData";
import { TransportNeedGraphPoint } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/types";
import { ADDITIONAL_FROM_TIME_MINUTES } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/constants";
import DecisionSupportContext from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/DecisionSupportContext";
import { useAlternativeTransportPlanningPassengerPredictions } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/useAlternativeTransportPassengerPredictions";
import { useContext, useMemo } from "react";
import { Nullish } from "shared/utils/objectUtils";

type TransportNeedData = {
  maxYValue: number;
  stretchData: {
    [stretchName: string]: TransportNeedGraphPoint[];
  };
  aggregatedData: TransportNeedGraphPoint[];
  status: "success" | "pending" | "error";
  refetch: () => void;
};

export function useTransportNeedData(
  incidentId: Nullish<string>,
): TransportNeedData {
  const { horizonToTime, horizonFromTime } = useContext(DecisionSupportContext);

  const {
    data: response,
    status,
    refetch,
  } = useAlternativeTransportPlanningPassengerPredictions(
    incidentId,
    subMinutes(horizonFromTime, ADDITIONAL_FROM_TIME_MINUTES),
    horizonToTime,
  );

  const stretchData = useMemo(() => {
    if (status === "success" && response?.data) {
      return transformPredictionsToChartData(response.data);
    }
    return {};
  }, [status, response]);

  const aggregatedData = useMemo(
    () =>
      response?.data.prediction.aggregatedPredictions.map((prediction) => ({
        date: new Date(prediction.fromTime),
        passengers: prediction.passengersRequiringTransport,
      })) ?? [],
    [response?.data.prediction.aggregatedPredictions],
  );

  const maxYValue = Math.round(
    aggregatedData.reduce(
      (prev, { passengers }) => (passengers > prev ? passengers : prev),
      0,
    ) * 1.05,
  );

  return {
    stretchData,
    aggregatedData,
    maxYValue,
    status,
    refetch,
  };
}
